import { Link } from "react-router-dom";
import { Shield, MessageSquare, Lock, Eye, ChevronRight, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-[#1a1a2e] text-lg">OKTENE</h1>
              <p className="text-xs text-slate-500">Canal de Ética</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 bg-[#f5a623]/10 text-[#1a1a2e] px-4 py-2 rounded-full text-sm font-medium">
                <Shield className="w-4 h-4 text-[#f5a623]" />
                Conformidade com a LGPD
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a1a2e] tracking-tight leading-tight">
                Canal de Ética e
                <span className="text-[#f5a623]"> Compliance</span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                A <strong>Ombudnow</strong> é uma empresa especializada em Ouvidoria e Denúncia Empresarial. 
                A <strong>Conterp</strong> está comprometida com a ética, transparência e conformidade, 
                e utiliza este canal terceiro e isento para gerenciar demandas de forma segura e confidencial.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 pt-4">
                <div className="flex-1 sm:flex-none">
                  <Link to="/denuncia">
                    <Button 
                      className="w-full bg-[#1a1a2e] hover:bg-[#1a1a2e]/90 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                      data-testid="denuncia-btn"
                    >
                      <Lock className="w-5 h-5 mr-2" />
                      Fazer Denúncia
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <ul className="text-sm text-slate-500 mt-3 space-y-1 pl-1">
                    <li>• Uso Anônimo ou identificado</li>
                    <li>• Uso para Colaboradores e público externo</li>
                  </ul>
                </div>
                <div className="flex-1 sm:flex-none">
                  <Link to="/ouvidoria">
                    <Button 
                      variant="outline"
                      className="w-full border-2 border-slate-200 hover:border-[#f5a623] px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:-translate-y-1"
                      data-testid="ouvidoria-btn"
                    >
                      <MessageSquare className="w-5 h-5 mr-2 text-[#f5a623]" />
                      Ouvidoria
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <ul className="text-sm text-slate-500 mt-3 space-y-1 pl-1">
                    <li>• Uso Identificado</li>
                    <li>• Uso Somente de colaboradores</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#f5a623]/20 to-[#1a1a2e]/20 rounded-3xl transform rotate-3"></div>
                <img 
                  src="https://images.unsplash.com/photo-1759956214507-af3b18cf292f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600&h=700&fit=crop"
                  alt="Plataforma de petróleo"
                  className="relative rounded-3xl shadow-2xl object-cover w-full h-[400px] md:h-[500px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1a1a2e] mb-4">
              Como funciona
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Nosso sistema garante total segurança e confidencialidade em todas as etapas
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="group relative overflow-hidden bg-white border border-slate-200 rounded-xl p-8 hover:border-[#f5a623]/50 transition-all duration-300 hover:shadow-lg">
              <div className="w-14 h-14 bg-[#1a1a2e] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a1a2e] mb-3">
                Anonimato Garantido
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Suas denúncias são completamente anônimas. Não rastreamos IP, não coletamos dados pessoais.
              </p>
            </div>

            {/* Card 2 */}
            <div className="group relative overflow-hidden bg-white border border-slate-200 rounded-xl p-8 hover:border-[#f5a623]/50 transition-all duration-300 hover:shadow-lg">
              <div className="w-14 h-14 bg-[#f5a623] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Eye className="w-7 h-7 text-[#1a1a2e]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a1a2e] mb-3">
                Acompanhamento Seguro
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Receba um protocolo e senha para acompanhar sua denúncia e comunicar-se com investigadores.
              </p>
            </div>

            {/* Card 3 */}
            <div className="group relative overflow-hidden bg-white border border-slate-200 rounded-xl p-8 hover:border-[#f5a623]/50 transition-all duration-300 hover:shadow-lg">
              <div className="w-14 h-14 bg-[#0EA5E9] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a1a2e] mb-3">
                Conformidade LGPD
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Todo o processo segue rigorosamente a Lei Geral de Proteção de Dados.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="conterp-gradient rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1759956214507-af3b18cf292f?crop=entropy&cs=srgb&fm=jpg&q=85')] opacity-10 bg-cover bg-center"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">
                Já fez uma denúncia?
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Use seu protocolo e senha para acompanhar o andamento do seu caso
              </p>
              <Link to="/denuncia/acompanhar">
                <Button 
                  className="bg-[#f5a623] hover:bg-[#f5a623]/90 text-[#1a1a2e] px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="acompanhar-btn"
                >
                  Acompanhar Denúncia
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a2e] text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#1a1a2e]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">OKTENE</h3>
                  <p className="text-xs text-white/60">Canal de Ética</p>
                </div>
              </div>
              <p className="text-white/70 text-sm max-w-md">
                Empresa especializada em Ouvidoria e Denúncia Empresarial, 
                oferecendo canais terceiros e isentos para gestão de compliance 
                com total segurança e confidencialidade.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Links</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/denuncia" className="hover:text-[#f5a623] transition-colors">Canal de Denúncias</Link></li>
                <li><Link to="/ouvidoria" className="hover:text-[#f5a623] transition-colors">Ouvidoria</Link></li>
                <li><Link to="/politica-privacidade" className="hover:text-[#f5a623] transition-colors">Política de Privacidade</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>support@ombudnow.com</li>
                <li>Salvador - BA</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8 text-center text-sm text-white/50">
            <p>© {new Date().getFullYear()} Ombudnow - Canal de Ética. Powered by Ombudnow.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
